<script setup lang="ts">
    import { computed, onMounted } from 'vue';
    import { useTheme } from 'vuetify';
    import { BetOrderStatus, OrderClosingCode } from '@/constants';
    import { OrderItem } from '@/models/order';
    import { orderService } from '@/services';
    import { dateFormatInUserTimeZone } from '@/helpers';
    import { failMessage, successMessage } from '@/helpers/message';

    const { tableHeight = undefined, orders = undefined } = defineProps<{
        tableHeight?: number;
        orders?: any[];
    }>();

    onMounted(() => {
        console.log('Component %s mounted.', 'app-eventOrdersSidePanel');
    });

    const { t } = useI18n();
    const theme = useTheme();

    async function confirm(item: OrderItem) {
        const response = await orderService.updateStatus(item.id, BetOrderStatus.Closing);

        if (response.data.success) {
            item.closingCode = response.data.closingCode!;
            switch (response.data.closingCode) {
                case OrderClosingCode.Successful:
                    successMessage(t('order.successUpdated'));
                    break;
                case OrderClosingCode.Failed:
                    failMessage(t('order.failedUpdated'));
                    break;
                case OrderClosingCode.InProgress:
                    failMessage(t('order.failedUpdatedBetInProgress'));
                    break;
            }
        }
    }

    const tableMinHeight = computed(() => {
        const height = (orders || []).length * 66 + 4; // 4 - extra margin bottom
        return height > Number(tableHeight) ? tableHeight : height;
    });
</script>

<template>
    <v-card elevation="0" style="overflow: hidden; border-radius: 8px">
        <v-card-title class="font14 pl-4 py-1 greyText font-weight-regular">
            <v-icon size="24" :icon="mdiClipboardClockOutline" />
            {{ t('eventStats.openOrders') }}
        </v-card-title>
        <v-card-text class="py-0 pt-3" style="overflow: auto" :style="'max-height: ' + tableHeight + 'px; min-height: ' + tableMinHeight + 'px'">
            <v-row v-for="(item, index) in orders" :key="index" class="px-1" style="height: 66px">
                <v-card :style="theme.name.value === 'dark' ? 'background-color: #323338!important' : ''" class="my-1" elevation="1" width="100%">
                    <v-card-text class="pl-2 py-2 font12">
                        <v-row class="pt-1 pl-7">
                            <v-spacer></v-spacer>
                            {{ dateFormatInUserTimeZone(item.createdAt, 'dd MMM HH:mm:ss') }}
                        </v-row>
                        <v-row :class="theme.name.value === 'dark' ? 'whiteText' : 'black--text'">
                            <v-col style="min-width: 75px; max-width: 75px" class="text-center pl-2 pr-0 pt-0 pb-2 d-flex justify-center align-center">
                                <span v-if="!item.isCancelAvailable" class="font10 orangeText">
                                    {{ t('panel.processing') }}
                                </span>
                                <span v-else-if="item.statusId === BetOrderStatus.Open && item.leftSeconds !== null && item.leftSeconds > 0" class="orangeText">
                                    {{ item.leftTime }}
                                    <v-btn
                                        tag="a"
                                        :disabled="!item.isCancelAvailable"
                                        :color="item.isCancelAvailable ? 'cancelOrderBtn' : 'cancelOrderBtnDisabled'"
                                        @click="confirm(item)"
                                        style="font-size: 10px; height: 15px; padding: 0 4px; width: 65px; letter-spacing: 0"
                                    >
                                        <span class="cancel-order" :class="item.isCancelAvailable ? '' : 'cancelOrderBtnDisabled'">{{ t('action.cancel') }}</span>
                                    </v-btn>
                                </span>
                                <span v-else-if="item.statusId === BetOrderStatus.Open && item.timeout === null" class="orangeText">
                                    <span>{{ t('panel.kickOff') }}</span>
                                    <br />
                                    <v-btn
                                        tag="a"
                                        dark
                                        :disabled="!item.isCancelAvailable"
                                        color="red"
                                        @click="confirm(item)"
                                        style="font-size: 10px; height: 15px; padding: 0 4px; width: 65px; letter-spacing: 0"
                                    >
                                        {{ t('action.cancel') }}
                                    </v-btn>
                                </span>
                                <span v-else class="font10 redText">
                                    {{ t('panel.closing') }}
                                </span>
                            </v-col>
                            <v-col :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-1 pr-0 pt-0 pb-2">
                                <div class="pr-1" v-html="item.getOrderNameExtended(true, false)"></div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<style scoped>
    :deep(.v-btn--disabled.v-btn--variant-elevated .v-btn__overlay) {
        opacity: 0;
    }
</style>
